<template>
  <div
    v-if="hasPartnerWorkflows"
    ref="partnerSelectionRoot"
    class="partner-selection"
  >
    <div class="primary-grey--text text-body-2 font-weight-bold mb-2">
      Trusted Partner Spotlight
    </div>
    <div class="primary-grey--text text-body-2 mb-4">
      Based on your question, we thought you might be interested to learn more about this partner
    </div>

    <GCarousel class="ma-2">
      <GCarouselItem
        v-for="partner in partnerWorkflows"
        :key="partner.id"
      >
        <v-card
          class="partner-card d-flex flex-column"
          outlined
        >
          <v-card-text class="pa-4 d-flex flex-column flex-grow-1">
            <div class="d-flex align-start mb-3">
              <v-avatar
                size="48"
                class="mr-3"
              >
                <v-img
                  v-if="partner.logo_url"
                  :src="partner.logo_url"
                  :alt="partner.name"
                  eager
                >
                  <template #placeholder>
                    <div class="d-flex align-center justify-center fill-height">
                      <v-progress-circular
                        indeterminate
                        size="20"
                        width="2"
                        color="grey lighten-1"
                      />
                    </div>
                  </template>
                </v-img>
                <v-icon
                  v-else
                  size="32"
                  color="grey"
                >
                  mdi-domain
                </v-icon>
              </v-avatar>
              <div class="partner-header-text">
                <div class="text-subtitle-1 font-weight-bold primary-grey--text">
                  {{ partner.name }}
                </div>
                <div
                  v-if="getDescriptor(partner)"
                  class="text-caption grey--text"
                >
                  {{ getDescriptor(partner) }}
                </div>
              </div>
            </div>

            <div
              v-if="getHeadline(partner)"
              class="text-body-2 font-weight-medium primary-grey--text mb-2 partner-headline"
            >
              {{ getHeadline(partner) }}
            </div>

            <div
              v-if="getBody(partner)"
              class="text-body-2 primary-grey--text partner-body"
            >
              {{ getBody(partner) }}
            </div>

            <div
              v-if="partner.ctas && partner.ctas.length > 0"
              class="d-flex pt-4 mt-auto"
              style="gap: 8px; width: 100%;"
            >
              <v-btn
                v-for="(cta, ctaIndex) in partner.ctas"
                :key="cta.id"
                :outlined="isOutlinedButton(ctaIndex, partner.ctas.length)"
                :color="COLORS.grainfoxAIButton"
                :class="{ 'white--text': !isOutlinedButton(ctaIndex, partner.ctas.length) }"
                small
                class="partner-cta-btn"
                style="flex: 1 1 0; min-width: 0;"
                @click="handleCtaClick(cta)"
              >
                <v-icon
                  v-if="getCtaIcon(cta.action_type)"
                  small
                  left
                >
                  {{ getCtaIcon(cta.action_type) }}
                </v-icon>
                {{ cta.name }}
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </GCarouselItem>
    </GCarousel>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { COLORS } from '../../helpers/colors';
import GCarousel from '../generic/GCarousel.vue';
import GCarouselItem from '../generic/GCarouselItem.vue';

const ACTION_TYPE_ICONS = {
  link: 'mdi-open-in-new',
  phone: 'mdi-phone',
  email: 'mdi-email',
};

export default {
  name: 'PartnerSelection',

  components: {
    GCarousel,
    GCarouselItem,
  },

  data() {
    return {
      COLORS,
    };
  },

  computed: {
    ...mapState('chatbot', ['partnerWorkflows']),
    ...mapGetters('chatbot', ['hasPartnerWorkflows', 'isStreaming']),
  },

  watch: {
    hasPartnerWorkflows(newVal) {
      if (newVal && !this.isStreaming) {
        this.emitScrollToPartners();
      }
    },

    isStreaming(newVal) {
      if (!newVal && this.hasPartnerWorkflows) {
        this.emitScrollToPartners();
      }
    },
  },

  methods: {
    ...mapActions('chatbot', ['trackCtaClick']),

    getDescriptor(partner) {
      return partner?.description?.descriptor || partner?.descriptor || null;
    },

    getHeadline(partner) {
      return partner?.description?.headline || partner?.headline || null;
    },

    getBody(partner) {
      return partner?.description?.body || partner?.body || null;
    },

    getCtaIcon(actionType) {
      return ACTION_TYPE_ICONS[actionType] || null;
    },

    handleCtaClick(cta) {
      if (cta?.id) {
        this.trackCtaClick(cta.id);
      }

      const actionValue = cta?.action_value;
      if (!actionValue) return;

      const actionType = cta?.action_type || 'link';

      switch (actionType) {
        case 'phone':
          window.open(`tel:${actionValue.replace(/\s/g, '')}`, '_self');
          break;
        case 'email':
          window.open(`mailto:${actionValue}`, '_self');
          break;
        case 'link':
        default:
          window.open(actionValue, '_blank', 'noopener,noreferrer');
          break;
      }
    },

    isOutlinedButton(index, totalCtas) {
      return totalCtas > 1 && index === 0;
    },

    emitScrollToPartners() {
      this.$nextTick(() => {
        if (this.$refs.partnerSelectionRoot) {
          this.$emit('scroll-to-partners', this.$refs.partnerSelectionRoot);
        }
      });
    },
  },
};
</script>

<style scoped lang="scss">
.partner-selection {
  margin-top: 16px;

  ::v-deep .g-carousel__items {
    justify-content: flex-start;
  }
}

.partner-card {
  border: 1px solid #12A8C4 !important;
  background-color: #E9F6FD !important;
  border-radius: 8px !important;
  width: 320px;
  min-width: 280px;
  max-width: 360px;
}

.partner-header-text {
  flex: 1;
  min-width: 0;
}

.partner-headline {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
}

.partner-body {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.5;
}

.partner-cta-btn {
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0;
  padding: 0 12px !important;
}

.partner-cta-btn .v-icon {
  margin-right: 6px;
}

.partner-cta-btn.v-btn--outlined:hover {
  background-color: rgba(18, 168, 196, 0.08) !important;
}

.partner-cta-btn:not(.v-btn--outlined):hover {
  background-color: #109bb5 !important;
}

@media (max-width: 600px) {
  ::v-deep .g-carousel {
    max-width: 100vw;
    overflow: hidden;
  }

  .partner-card {
    width: 260px !important;
    min-width: 260px !important;
    max-width: 260px !important;
  }

  ::v-deep .g-carousel__items {
    max-width: calc(100vw - 48px);
  }
}
</style>
